import {useMemo, useContext} from 'react'
import {GTMContext} from '../contexts'
import {commonParams} from '../utils/gtm-utils'

export class GtmAPI {
    constructor(gtmContext) {
        this.gtmContext = gtmContext
    }

    /**
     * Constructs an object containing the product Id.
     */
    _constructProduct(item) {
        return {
            brand: item?.brand ?? item?.productId,
            category: item?.c_subcategory ?? item.c_subcategory,
            categoryID: item?.primaryCategoryId ?? item.representedProduct?.c_category,
            id: item?.id ?? item?.productId,
            name: item?.name ?? item?.productName,
            price: item?.price,
            quantity: 1
        }
    }

    /**
     * Pushes data to gtm
     *
     * @param {object} data - Object to be pushed to gtm
     * @returns {object} - sent object
     */
    _pushLayer = (dataLayer) => {
        if (dataLayer) {
            window.dataLayer.push(dataLayer)
        }
    }

    /*
     * Push oneTrust data to GTM
     */
    async sendOntrust() {
        // Create a map to track unique OneTrust events
        let uniqueEvents = {}
        let filteredDataLayer = []
        window.dataLayer.forEach(function (item) {
            if (
                item.event &&
                (item.event === 'OneTrustLoaded' ||
                    item.event === 'OptanonLoaded' ||
                    item.event === 'OneTrustGroupsUpdated')
            ) {
                var uniqueKey =
                    item.event + (item.OnetrustActiveGroups || item.OptanonActiveGroups || '')

                // Only add the event if it hasn't been seen before
                if (!uniqueEvents[uniqueKey]) {
                    uniqueEvents[uniqueKey] = true
                    filteredDataLayer.unshift(item)
                }
            } else {
                filteredDataLayer.push(item)
            }
        })

        // Ensure the OneTrust object is at the beginning
        var existingOneTrust = filteredDataLayer.find(function (item) {
            return item.Onetrust
        })

        if (!existingOneTrust && window.OneTrust) {
            filteredDataLayer.unshift({
                Onetrust: window.OneTrust
            })
        }

        // Replace the original dataLayer with the filtered version
        window.dataLayer = filteredDataLayer
    }

    /**
     * This tells the GTM about Home page viewed
     */
    async sendHomePageView(event) {
        const params = await commonParams(this.gtmContext)
        const dataLayer = {
            event: event,
            ...params,
            ecommerce: {
                currency: this.gtmContext?.currency
            }
        }

        this._pushLayer(dataLayer)
    }

    /**
     * This tells the GTM about Home page viewed
     */
    async sendSearchImpressionData(event) {
        const params = await commonParams(this.gtmContext)
        const dataLayer = {
            event: event,
            ...params,
            ecommerce: {
                currency: this.gtmContext?.currency
            }
        }

        this._pushLayer(dataLayer)
    }

    /**
     * This tells the GTM about Home page viewed
     */
    async sendPdpViewItem(event, product) {
        const [params, productData] = await Promise.all([
            commonParams(this.gtmContext),
            this._constructProduct(product)
        ])
        const dataLayer = {
            event: event,
            ...params,
            detail: {
                list: 'Product Detail Page'
            },
            ecommerce: {
                currency: this.gtmContext?.currency,
                contentid: product?.id
            },
            products: [productData]
        }

        this._pushLayer(dataLayer)
    }

    /**
     * This tells the GTM about adding product to Cart
     */
    async sendAddToCart(event, product) {
        const productData = this._constructProduct(product)
        const dataLayer = {
            event: event,
            ecommerce: {
                add: {
                    products: [productData]
                }
            }
        }

        this._pushLayer(dataLayer)
    }

    /**
     * This tells the GTM about adding product to wishlsit
     */
    async sendProductToWishList(event, product) {
        const productData = this._constructProduct(product)
        const dataLayer = {
            event: event,
            ecommerce: {
                add: {
                    products: [productData]
                }
            }
        }

        this._pushLayer(dataLayer)
    }
}

/**
 * This is hook executes GTM events
 * @returns events
 */
export const useGtm = () => {
    const gtmContext = useContext(GTMContext)

    const gtmInstance = useMemo(() => {
        if (!gtmContext) return null
        return new GtmAPI(gtmContext)
    }, [gtmContext])

    return useMemo(() => {
        if (!gtmInstance) return null

        return {
            sendHomePageView: (event) => gtmInstance.sendHomePageView(event),
            sendOneTrustData: () => gtmInstance.sendOntrust(),
            sendSearchImpressionData: (event) => gtmInstance.sendSearchImpressionData(event),
            sendPdpViewItem: (event, product) => gtmInstance.sendPdpViewItem(event, product),
            sendAddToCart: (event, product) => gtmInstance.sendAddToCart(event, product),
            sendProductToWishList: (event, product) =>
                gtmInstance.sendProductToWishList(event, product)
        }
    }, [gtmInstance, gtmContext])
}
